/**
 * Site Diary entity DTOs.
 *
 * Mirrors Prisma `SiteDiaryEntry` (prisma/schema.prisma:1131). Photos are
 * stored as plain URLs on a `String[]` column (DO Spaces). The mobile
 * upload flow uses the presigned signer (Phase 05) — the URL returned by
 * the commit step is what lands in this array.
 *
 * Ids are cuid() per the Prisma model.
 */
import { z } from "zod";

// PostgreSQL `Int` is signed 32-bit. `.int()` alone still admits any safe JS
// integer, so 2_147_483_648 sailed past validation and died in Prisma as a 500
// (Codex round 8). Bounds are the DB range, not a product opinion. The
// constants live in ../common so other DTO mirrors share them.
import { INT32_MAX, INT32_MIN, entityIdSchema } from "../common";

export const WEATHER_CONDITIONS = [
  "CLEAR",
  "CLOUDY",
  "RAIN",
  "HEAVY_RAIN",
  "WIND",
  "FROST",
  "SNOW",
  "FOG",
] as const;

export type WeatherCondition = (typeof WEATHER_CONDITIONS)[number];

export const WeatherConditionSchema = z.enum(WEATHER_CONDITIONS);

export const DiaryEntrySchema = z
  .object({
    id: entityIdSchema,
    projectId: entityIdSchema,
    // Recorder provenance, not ownership — null once the author deleted their
    // account (SiteDiaryEntry.userId is SetNull so evidence outlives authors).
    userId: z.string().nullable(),
    date: z.string().datetime(),
    weather: WeatherConditionSchema,
    temperature: z.number().int().min(INT32_MIN).max(INT32_MAX).nullable(),
    windSpeedMph: z.number().int().nullable(),
    workersOnSite: z.number().int().min(0).max(INT32_MAX),
    progressNotes: z.string(),
    issuesNoted: z.string().nullable(),
    visitorsOnSite: z.string().nullable(),
    delaysNoted: z.string().nullable(),
    photoUrls: z.array(z.string()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  });

export type DiaryEntry = z.infer<typeof DiaryEntrySchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the routes serialise through the Strict form.
export const DiaryEntryStrict = DiaryEntrySchema.strict();

/**
 * POST /api/mobile/v1/projects/:projectId/diary body. `photoUrls` accepts
 * the URLs returned by the presigned upload commit endpoint (Phase 07).
 */
export const DiaryEntryCreateRequest = z
  .object({
    date: z.string().datetime(),
    weather: WeatherConditionSchema.default("CLEAR"),
    temperature: z.number().int().min(INT32_MIN).max(INT32_MAX).nullable().optional(),
    windSpeedMph: z.number().int().min(0).max(150).nullable().optional(),
    workersOnSite: z.number().int().min(0).max(INT32_MAX).default(0),
    progressNotes: z.string().min(1),
    issuesNoted: z.string().nullable().optional(),
    visitorsOnSite: z.string().nullable().optional(),
    delaysNoted: z.string().nullable().optional(),
    photoUrls: z.array(z.string()).default([]),
  })
  .strict();

export type DiaryEntryCreateRequest = z.infer<typeof DiaryEntryCreateRequest>;

/**
 * PATCH /api/mobile/v1/projects/:projectId/diary/:entryId body.
 *
 * `expectedUpdatedAt` is the OPTIONAL optimistic-concurrency claim (#2369):
 * when present, the server only applies the edit if the row's `updatedAt`
 * still equals it, answering 409 CONFLICT otherwise. Absent — the shape every
 * pre-claim build sends — the edit behaves exactly as before (web parity,
 * older builds). It is a claim about the row's version, never a field to
 * write: the route strips it before building the patch.
 */
export const DiaryEntryUpdateRequest = DiaryEntryCreateRequest.partial().extend({
  expectedUpdatedAt: z.string().datetime().optional(),
});

export type DiaryEntryUpdateRequest = z.infer<typeof DiaryEntryUpdateRequest>;

/**
 * POST /api/mobile/v1/projects/:projectId/diary/:entryId/photos request.
 * Adds N photo URLs to an existing diary entry (replaces is also allowed —
 * pass `replace: true`).
 */
export const DiaryPhotoAttachRequest = z
  .object({
    photoUrls: z.array(z.string()).min(1),
    replace: z.boolean().default(false),
  })
  .strict();

export type DiaryPhotoAttachRequest = z.infer<typeof DiaryPhotoAttachRequest>;
